import React from "react";
import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CloseIcon from "@mui/icons-material/Close";

import { KMKDialog } from "../../Common";
import { DATA_SOURCE } from "../../../util/Data/DataModeling";

const ConflictingNameObjectsCols = ["System Name", "Source", "Entity"];

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  dialogPaper: {
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item": {
      paddingTop: "15px",
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiDivider-root": {
      marginTop: "5px",
      marginBottom: "9px",
    },
    "& .MuiDialogActions-root": {
      padding: "0 20px 10px 0",
    },
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
    minHeight: "110px",
    maxHeight: "400px",
    overflow: "auto",
  },
  deleteReffConfirm: {
    "& span": {
      display: "block",
    },
  },
  list: {
    listStyleType: "disc",
    margin: "8px 0px",
    paddingLeft: 32,
    background: "#f0f0f0",
  },
  listItem: {
    display: "list-item",
    padding: 0,
  },
}));

const InvalidConnectionPopup = ({
  reservedSources,
  reservedEntities,
  IsDeletedInvalidSources,
  conflictingNames,
  closePopup,
}) => {
  const { classes } = useStyles();

  //Sorting alphabetically
  IsDeletedInvalidSources.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  let reservedKeywordErrorDisplay = (typeOfError, mappings) => {
    return (
      <>
        {mappings.map((reserved, i) => (
          <React.Fragment key={i}>
            <span style={{ backgroundColor: "white" }}>{`${
              typeOfError === "reservedSources" ? DATA_SOURCE.singular : "Entity"
            }: ${typeOfError === "reservedSources" ? reserved.source : reserved.entity}`}</span>
            <List className={classes.list} sx={{ listStyleType: "unordered" }} dense={true}>
              {reserved.reservedFields?.map((field, j) => (
                <ListItem className={classes.listItem} key={j}>
                  <ListItemText primary={field.name} />
                </ListItem>
              ))}
            </List>
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <KMKDialog className={classes.dialogPaper} open={true} maxWidth="md" onClose={closePopup}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>
          <ReportProblemIcon color="warning" />
          Inaccurate Data Model
        </Toolbar>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box
        sx={{
          width: "100%",
        }}
        className={classes.container}
      >
        <Box component="form" className={classes.formContainer}>
          <DialogContent style={{ gap: "20px" }}>
            {IsDeletedInvalidSources.length !== 0 && (
              <>
                <span className={classes.deleteReffConfirm}>
                  <span>
                    {`The listed ${DATA_SOURCE.singular}(s) have more than one deletion flag configured fields, causing an issue:`}
                  </span>
                  <List className={classes.list} dense={true}>
                    {IsDeletedInvalidSources.map((data_source) => (
                      <ListItem className={classes.listItem} key={data_source}>
                        <ListItemText primary={data_source} />
                      </ListItem>
                    ))}
                  </List>
                  <span>
                    Please make sure you have only 1 deletion flag configured field to apply the model to your data
                    warehouse.
                  </span>
                </span>
              </>
            )}
            {(reservedSources.length !== 0 || reservedEntities.length !== 0) && (
              <>
                {IsDeletedInvalidSources.length !== 0 && <hr></hr>}
                <span className={classes.deleteReffConfirm}>
                  <span>Reserved keyword(s) have been found in the attribute name(s), causing an issue:</span>
                  <Box sx={{ margin: "10px 0 0 10px" }}>
                    {reservedSources.length ? reservedKeywordErrorDisplay("reservedSources", reservedSources) : <></>}
                    {reservedEntities.length ? (
                      reservedKeywordErrorDisplay("reservedEntities", reservedEntities)
                    ) : (
                      <></>
                    )}
                  </Box>
                  <span>
                    Please make sure you update the attribute name(s) listed above to apply the model to your data
                    warehouse.
                  </span>
                </span>
              </>
            )}
            {conflictingNames.length !== 0 && (
              <>
                {(IsDeletedInvalidSources.length !== 0 || reservedSources.length !== 0) && <hr></hr>}
                <span className={classes.deleteReffConfirm}>
                  <span>{`The listed system name(s) are common to the following ${DATA_SOURCE.plural} and Entities:`}</span>
                  <TableContainer sx={{ margin: "8px 0px", background: "#f0f0f0" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {ConflictingNameObjectsCols.map((col) => (
                            <TableCell key={col} sx={{ fontWeight: "700" }}>
                              {col}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {conflictingNames.map(({ name, source, entity }) => (
                          <TableRow key={name} sx={{ wordBreak: "break-all" }}>
                            <TableCell>{name}</TableCell>
                            <TableCell>{source}</TableCell>
                            <TableCell>{entity}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <span>
                    {`Please add a unique system name for either the ${DATA_SOURCE.singular} or Entity to apply the data
                    to warehouse.`}
                  </span>
                </span>
              </>
            )}
          </DialogContent>
        </Box>
      </Box>
    </KMKDialog>
  );
};

export default InvalidConnectionPopup;
